import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18n from '@/i18n/i18n';

/** Lazy import to avoid circular dependency with authStore */
const getAuthStore = () => import('@/store/authStore').then((m) => m.useAuthStore);

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Attach JWT access token and language to every outgoing request. */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authStore = await getAuthStore();
    const token = authStore.getState().accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['Accept-Language'] = i18n.language || 'en';

    return config;
  },
  (error) => Promise.reject(error),
);

/** Track whether a token refresh is already in progress. */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

function processQueue(error: Error | null, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error ?? new Error('Token refresh failed'));
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

/**
 * On 401 responses, attempt a token refresh via /api/auth/token/refresh/.
 * If the refresh succeeds, replay the original request with the new token.
 * If it fails, clear auth state and redirect to the login page.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 429 Too Many Requests (throttled)
    if (error.response?.status === 429) {
      const data = error.response.data as Record<string, unknown>;
      const detail = typeof data?.detail === 'string' ? data.detail : '';
      const seconds = detail.match(/(\d+)\s*second/)?.[1];
      const { toast } = await import('sonner');
      const t = i18n.t.bind(i18n);
      if (seconds) {
        toast.error(t('common.errors.throttledWithTime', { seconds }));
      } else {
        toast.error(t('common.errors.throttled'));
      }
      return Promise.reject(error);
    }

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const authStore = await getAuthStore();
    const { refreshToken, setTokens, clearAuth } = authStore.getState();

    if (!refreshToken) {
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<{ access: string; refresh?: string }>(
        `${apiClient.defaults.baseURL}/auth/token/refresh/`,
        { refresh: refreshToken },
      );

      const newAccessToken = data.access;
      const newRefreshToken = data.refresh ?? refreshToken;

      setTokens(newAccessToken, newRefreshToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);

      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError as Error, null);
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
